import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import { AlertNotificationModel } from '../models/alert.model'
import { AutoAlertKeywordModel, RuleModel } from '../models/auto-alert.model'
import {
    Contact, ContactType, ConversationModel, DeletedGroupContactData, Group, GroupChatModel, OutLookResponse,
    pushConversation,
    pushConversationInContact,
    pushHistory,
    sortContacts,
    sortConversations,
    TypeAction,
    TypingDataModel, updateContactsInConversations, updateContactsInGroup, updateContactTypesInConversations, updateGroupsInContacts,
} from '../models/conversations'
import { FeaturesModel } from '../models/features.model'
import { KafkaModel } from '../models/kafka.model'
import { NewOrganization, OrganizationModel } from '../models/organization.model'
import { PhoneNumbergroup, PhoneNumberModel, ProviderModel } from '../models/phone-number.model'
import { mapToOrgPlanTracker, mapToPlanMetrics, mapToUserPlanTracker, OrgPlanTracker, PlanMetrics, UserPlanTracker } from '../models/plan-metrics.model'
import { Plan, PlanModel } from '../models/plans.model'
import { ProfileModel } from '../models/profile.model'
import { BulkImportReportModel, ImportSource, ImportType, mapToIntegrationReport, mapToBulkImportReport, ReportsModel } from '../models/reports.model'
import { RoleModel } from '../models/roles.model'
import { ArchiveConfigurableDaysSetting, LoginType, LoginTypeModel, LowCreditSettingsModel, Setting, SettingsModel } from '../models/settings-model'
import { TemplateModel } from '../models/templates.models'
import { PlanHistoryModel } from '../models/transaction-history.model'
import { UserModel } from '../models/users.model'
import { SettingsService } from '../services/SettingsService'
import { UserService } from '../services/UserService'
import { CustomAttributeDefinitionModel } from '../models/custom-attribute.model'
import { AppState } from './models'
import { FaceRegistrationInviteLinkStatus } from '../models/secure-file'
import { LocalStorage, UtilityService } from '../services/UtilityService'
import { mapToScheduledMessage, ScheduleMessageStatus } from '../models/conversations/schedule-messages.model'
import { SecureDocModel } from '../models/secure-docs.model'
import { Routes } from '../components/routes/routes.config'
import { RouteModel } from '../models/routes.model'
import { MobileRoutes } from '../components/routes/mobile-routes.config'
import { TeamRoutes } from '../components/routes/teams-routes.config'
import { TeamsMobileRoutes } from '../components/routes/teams-mobile-routes.config'
import { WebFormModel } from '../models/web-form.model'

const initialState: AppState = {
    userRoleId: UserService.getLoggedInUserRole() ?? 0,
    Conversations: [],
    SecureChatConversations: [],
    Organizations: [],
    OrganizationDetails: [],
    History: {},
    ChatSummaryHistory: {},
    ContactTypes: [],
    Contacts: [],
    CustomAttributeDefinitions: [],
    // AllContacts: [],
    Users: [],
    AllUsers: [],
    Groups: [],
    Features: [],
    Settings: {
        // BlockedContacts: [],
        // UnSubscribedContacts: [],
        AllowedWrongPasswordAttempts: 0,
        NotificationsEnabled: false,
        TwoFactAuthEnabled: false,
        UnSubscribeStopEnabled: false,
        BlockIncomingMMSEnabled: false,
        BlockIncomingMessageLinksEnabled: false,
        Settings: [],
        VoiceVerificationEnabled: true,
    } as SettingsModel,
    Profile: {
        FullName: 'U K',
        Email: '',
        FirstName: '',
        ImageReferenceId: '',
        LastName: '',
        PhoneNumber: '',
        OrganizationName: '',
        OrgId: JSON.parse(localStorage.getItem("current-user")?.toString() || "{}")
            ?.organizationId || '',
        RoleID: '',
    } as ProfileModel,
    IsMobileDevice: window.outerWidth <= 835,
    ShowSideMenu: false,
    IsTeamsApp: window.location.pathname.includes('teams'),
    DeviceWidth: window.outerWidth,
    DeviceHeight: window.innerHeight,
    PhoneNumbers: [],
    Roles: [],
    Plans: [],
    Providers: [],
    AlertNotifications: [],
    UserAlertNotifications: [],
    RulesKeywords: [],
    Templates: [],
    AllKeywords: [],
    PrivateRules: [],
    AllPrivateRules: [],
    GlobalRules: [],
    AllGlobalRules: [],
    Reports: [],
    PlanHistory: [],
    GetBlockedContacts: [],
    GetUnSubscribedContacts: [],
    GroupContactsDeletionData: [],
    TypingData: [],
    SecureFiles: [],
    FaceAuthenticationFiles: [],
    SecureFileLinks: [],
    LoadingConversations: false,
    LoadingSecureChatConversations: false,
    LoadingHistory: false,
    LoadingContacts: false,
    LoadingContactTypes: false,
    LoadingUsers: false,
    LoadingGroups: false,
    LoadingOrganizations: false,
    LoadingSettings: false,
    LoadingProfile: false,
    LoadingFeatures: false,
    LoadingRoles: false,
    LoadingPhoneNumbers: false,
    LoadingPlans: false,
    LoadingProviders: false,
    LoadingOrganizationDetails: false,
    LoadingAlertsNotications: false,
    LoadingAutoAlertKeywords: false,
    LoadingTemplates: false,
    LoadedConversations: false,
    LoadedHistory: false,
    LoadedContacts: false,
    LoadedContactTypes: false,
    LoadedUsers: false,
    LoadedGroups: false,
    LoadedOrganizations: false,
    LoadedSettings: false,
    LoadedProfile: false,
    LoadedFeatures: false,
    LoadedRoles: false,
    LoadedPhoneNumbers: false,
    LoadedPlans: false,
    LoadedProviders: false,
    LoadedOrganizationDetails: false,
    LoadingAutomationPrivateRules: false,
    LoadingAutomationGlobalRules: false,
    LoadingReports: false,
    LoadingKafka: false,
    Kafka: [],
    LoadingSecureFiles: false,
    LoadingSecureFileLinks: false,
    IsMassContactsDeletionInProgress: false,
    IsMassGroupsDeletionInProgress: false,
    HandleMassContactsActionEvent: false,
    HandleMassGroupsActionEvent: false,
    NewMessageRecived: [],
    PlanMetrics: mapToPlanMetrics(
        JSON.parse(localStorage.getItem("current-user")?.toString() || "{}").planTracker
    ),
    ShowingAlertsCount: 0,
    Toasts: [],
    ExportMessageLogs: [],
    SecureChatExportMessageLogs: [],
    IsMessageLogDownloadReportMsg: '',
    WebNotificationText: {},
    OrganizationName: "",
    LoadingscheduleMessages: false,
    ScheduleMessages: [],
    DataLoading: false,
    PhonenumberSelectedOrgIDs: [],
    UsersSelectedOrgIDs: [],
    OrganizationsphoneNumbers: [],
    OrganizationsUsers: [],
    ActivityFeedNotificationStatus: {
        integrationStatus: false,
        message: "",
        notificationStatus: false
    },
    HeaderSelectedCount: 0,
    BusinessHoursStatus: false,
    LoadingFaceAuthenticationFiles: false,
    NotificationStatus: {
        phoneNumber: "",
        userId: "",
        mobileNotificationStatus: false,
        teamsNotificationStatus: false
    },
    WebexNotificationStatus: {
        status: false
    },
    IsFalkonSignNotification: false,
    AssignToUser: {
        AssignedDateTime: '',
        AssignedToId: '',
        ContactNumber: '',
        CreatedById: '',
        MessageDateTime: '',
        PhoneNumberId: '',
        Status: '',
        msgType: 'ticket',
        IsStatusChange: false,
    },
    ContactStatusHistory: {},
    LowCreditSettings: {
        GroupCredits: 0,
        NormalCredits: 0,
        EmailAlertEnabled: false
    },
    FalkonWebSocket: null,
    WebSocketConnection: false,
    IscontactsSyncing: false,
    BulkImportSyncDetails: {
        TotalRecords: 0,
        SavedRecords: 0,
        ExistingRecords: 0,
        IgnoredRecords: 0,
        Source: ImportSource.Excel,
        Type: ImportType.Contacts,
        ReportId: "",
        Flag: false,
        Error: "",
        // loadContacts: false,
    },
    isSignalRConnected: false,
    isDarkMode: false,
    TrainChatHistoryStatus: "",
    AiTrainedFiles: [],
    AiTrainedUrls: [],
    PhoneNumberGroups: [],
    OrganzationPhoneNumberGroups: [],
    PhoneGroupsSelectedOrgIDs: [],
    SecureDocs: [],
    LoadingSecureDocs: false,
    LoadedSecureDocs: false,
    LoadingNewPlans: false,
    newPlans: [],
    NewOrganizations: [],
    LoadingNewOrganizations: false,
    IsNewOrgFlow: LocalStorage.get("current-user")?.isNewSubFlow,
    UserPlanTracker: mapToUserPlanTracker(
        LocalStorage.get('current-user')?.userPlanTracker || {}
    ),
    OrgPlanTracker: mapToOrgPlanTracker(
        LocalStorage.get('current-user')?.orgPlanTracker || {}
    ),
    LoadedNewPlans: false,
    Routes: Routes(),
    OptInCollectedMessage: "",
    IsScheduleMessageImporting: false,
    SecureChatScheduleMessages: [],
    ConversationsAssignStatus: {},
    NewSubscriptionOrganizationsphoneNumbers: [],
    NewSubscriptionPhonenumberSelectedOrgIDs: [],
    NewSubscriptionSelectedUsersOrgIds: [],
    NewSubscriptionOrganizationsUsers: [],
    IsGroupContactsSyncing: false,
    FalkonEsignTemplates: [],
    WebForms: [],
    OrganizationLoginType: { SSOMandate: false, SSOType: LoginType.None },
    ArchiveConfigurableDaysSetting: {
        IsEnabled: false,
        NumberOfDays: 0,
        OrgId: ""
    }
}

const counterSlice = createSlice({
    name: 'appReducer',
    initialState,
    reducers: {
        updateUserRoleId: (state: AppState, action: PayloadAction<number>) => {
            state.userRoleId = action.payload
        },

        updateConversations: (state: AppState, action: PayloadAction<ConversationModel[]>) => {
            state.Conversations = updateContactTypesInConversations(
                updateContactsInConversations(action.payload, state.Contacts),
                state.ContactTypes
            );
            state.LoadingConversations = false;
            state.LoadedConversations = true;
        },

        updateSecureChatConversations: (state: AppState, action: PayloadAction<{ data: ConversationModel[], refresh?: boolean }>) => {

            if (action.payload.refresh) {
                state.SecureChatConversations = sortConversations(action.payload.data)
                state.LoadingSecureChatConversations = false;
            }
            else {
                let filteredData = state.SecureChatConversations.filter(x => x.ID != action.payload.data[0].ID)
                state.SecureChatConversations = sortConversations([...filteredData, action.payload.data[0]])
            }

        },

        UpdateWebSocket: (state: AppState, action: PayloadAction<WebSocket | null>) => {
            state.FalkonWebSocket = action.payload
        },

        UpdateWebSocketConnection: (state: AppState, action: PayloadAction<boolean>) => {
            state.WebSocketConnection = action.payload
        },

        updateMuteConversations: (state: AppState, action: PayloadAction<any[]>) => {
            state.Conversations.forEach((conv) => {
                const found = action.payload.find(el => el.phoneNumber === conv.PhoneNumber);
                const currentTime = UtilityService.getPreferredTimeZoneTimeNow()
                if (!!found) {
                    let muteTime = new Date(found.muteDateTime)
                    muteTime.setSeconds(muteTime.getSeconds() - 1);
                    conv.IsMuted = muteTime >= currentTime
                }
            })
        },

        updatePinConversations: (state: AppState, action: PayloadAction<any[]>) => {
            state.Conversations.forEach((conv) => {
                const found = action.payload.some(el => el.phoneNumber === conv.PhoneNumber);
                conv.IsPinned = found
            })
        },

        updateArchiveConversations: (state: AppState, action: PayloadAction<any[]>) => {
            state.Conversations.forEach((conv) => {
                const found = action.payload.some(el => el.phoneNumber === conv.PhoneNumber && el.isArchived);
                conv.IsArchived = found
            })
        },

        updateLoadingConversations: (state: AppState, action: PayloadAction<boolean>) => {
            state.LoadingConversations = action.payload;
        },

        updateLoadingSecureChatConversations: (state: AppState, action: PayloadAction<boolean>) => {
            state.LoadingSecureChatConversations = action.payload;
        },

        updateDataLoading: (state: AppState, action: PayloadAction<boolean>) => {
            state.DataLoading = action.payload;
        },

        updateHistory: (state: AppState, action: PayloadAction<{ phoneNumber: string, history: ConversationModel[] }>) => {
            state.History = {
                ...state.History,
                [action.payload.phoneNumber]: action.payload.history
            }
        },

        updateChatSummaryHistory: (state: AppState, action: PayloadAction<{ phoneNumber: string, ChatSummary: { Sentiment: string, Summary: string } }>) => {
            state.ChatSummaryHistory = {
                ...state.ChatSummaryHistory,
                [action.payload.phoneNumber]: action.payload.ChatSummary
            }
        },

        updateContactHistory: (state: AppState, action: PayloadAction<any>) => {
            state.ContactStatusHistory = {
                ...state.ContactStatusHistory,
                [action.payload.phoneNumber]: [...action.payload.history]
            }
        },

        updateContactStatusHistory: (state: AppState, action: PayloadAction<any>) => {
            let history = state.ContactStatusHistory[action.payload.phoneNumber] || []
            state.ContactStatusHistory = {
                ...state.ContactStatusHistory,
                [action.payload.phoneNumber]: [...history, ...action.payload.history]
            }
        },

        clearHistory: (state: AppState, action: PayloadAction<{ [key: string]: ConversationModel[] }>) => {
            state.History = {}
            state.ContactStatusHistory = {}
        },

        cleanHistory: (state: AppState, action: PayloadAction<string>) => {
            const phoneNumber = (action.payload || '').replace('+', '')

            let history = state.History[phoneNumber]?.filter((x) => x.ID) || []
            if (history.length > 0) {
                let oldCon = state.Conversations.find(
                    (x) => x.PhoneNumber === action.payload
                )
                let newCon = history[history.length - 1]
                if (oldCon && newCon) {
                    newCon.Contact = oldCon.Contact
                }
                let updatedConversations =
                    history.length > 0
                        ? [
                            ...state.Conversations?.filter(
                                (x) => x.PhoneNumber !== action.payload
                            ),
                            newCon,
                        ]
                        : []
                state.Conversations = sortConversations(updatedConversations);
                state.History = {
                    ...state.History,
                    [phoneNumber]: history,
                };
            }
        },

        updateContacts: (state: AppState, action: PayloadAction<Contact[]>) => {
            // let contactData = action.payload.filter((x: Contact, index: number) => action.payload.findIndex((y: Contact) => y.ID === x.ID) === index).filter((x: Contact) => !(x.IsDeleted || !x.FirstName));
            // state.AllContacts = action.payload;
            state.Contacts = sortContacts(updateGroupsInContacts(action.payload, state.Groups));
            state.Groups = updateContactsInGroup(state.Groups, action.payload);
            // state.Settings.BlockedContacts = action.payload?.filter((x: Contact) => x.IsBlocked);
            // state.Settings.UnSubscribedContacts = action.payload?.filter((x: Contact) => x.IsUnsubscribed);
            state.LoadingContacts = false;
            state.LoadedContacts = true;
            state.IsMassContactsDeletionInProgress = false;
            // hit end points layoutcomponent
            // state.GetBlockedContacts = action.payload?.filter((x: Contact) => x.IsBlocked)
            // state.GetUnSubscribedContacts = action.payload?.filter((x: Contact) => x.IsUnsubscribed)
        },

        updateLoadingContacts: (state: AppState, action: PayloadAction<boolean>) => {
            state.LoadingContacts = action.payload
        },

        updateCustomAttributeDefinitions: (
            state: AppState,
            action: PayloadAction<{
                data: any;
                refresh: boolean;
            }>
        ) => {
            const { data, refresh } = action.payload;
            if (refresh) {
                state.CustomAttributeDefinitions = data as CustomAttributeDefinitionModel[];
            }
            else {
                const existing = state.CustomAttributeDefinitions ?? [];
                const filtered = existing.filter((item) => item.Id !== data.Id);
                state.CustomAttributeDefinitions = data.IsDeleted
                    ? filtered
                    : [...filtered, data];
            }
        },

        updateContactTypes: (state: AppState, action: PayloadAction<ContactType[]>) => {
            state.ContactTypes = action.payload
        },

        updateContactsInConversation: (state: AppState, action: PayloadAction<Contact[]>) => {
            state.Conversations = updateContactsInConversations(
                state.Conversations,
                state.Contacts
            );
        },

        updateContactTypesInConversation: (state: AppState, action: PayloadAction<ContactType[]>) => {
            state.Conversations = updateContactTypesInConversations(
                state.Conversations,
                action.payload
            );
        },

        pushConversations: (state: AppState, action: PayloadAction<ConversationModel[]>) => {
            state.Conversations = pushConversation(
                state.Conversations,
                state.Contacts,
                action.payload
            );
            state.History = pushHistory(state.History, action.payload);
            state.NewMessageRecived = action.payload
            if (!action.payload[0]?.IsOutbound) {
                state.Contacts = pushConversationInContact(state.Contacts, action.payload);
                state.Groups = updateContactsInGroup(state.Groups, state.Contacts)
            }
        },

        updateNewMessageRecived: (state, action) => {
            state.NewMessageRecived = action.payload
        },

        pushContacts: (state: AppState, action: PayloadAction<Contact[]>) => {
            // let { Conversations, Contacts } = state;
            // action.payload?.forEach((contact: Contact) => {
            //     let _conversation = Conversations?.find(x => x.PhoneNumber === contact.PhoneNumber || x.Contact?.ID === contact.ID);
            //     let _contact = Contacts?.find(x => x.ID === contact.ID);
            //     if (_conversation) {
            //         _conversation.UnReadCount = contact.UnReadCount;
            //         _conversation.IsBlocked = contact.IsBlocked;
            //     }

            //     if (_conversation?.Contact?.ID === contact.ID && (contact.IsDeleted || contact.MaskedPhoneNumber !== _conversation?.Contact?.MaskedPhoneNumber)) {
            //         _conversation.Contact = undefined;
            //     }

            //     if (_contact) {
            //         if (_contact.IsBlocked !== contact.IsBlocked) {
            //             _contact.IsBlocked = contact.IsBlocked;
            //             _contact.CreatedDateTime = contact.CreatedDateTime;
            //         }
            //         _contact.UnReadCount = contact.UnReadCount;
            //         _contact.IsDeleted = contact.IsDeleted;
            //     }
            // });
            // if (!!state.Contacts?.filter((x) => action.payload.map(y => y.ID)?.includes(x.ID))) {
            //     state.Contacts = sortContacts([
            //         ...state.Contacts?.filter((x) => !action.payload.map(y => y.ID)?.includes(x.ID)),
            //         ...action.payload,
            //     ]?.filter((x: Contact) => !(x.IsDeleted || !x.FirstName)));
            //     state.AllContacts = [
            //         ...state.AllContacts?.filter((x) => !action.payload.map(y => y.ID)?.includes(x.ID)),
            //         ...action.payload,
            //     ]
            // }
            // else {
            //     state.Contacts = sortContacts([...Contacts, ...action.payload?.filter((x: Contact) => !Contacts?.map(y => y.ID)?.includes(x.ID))]?.filter((x: Contact) => !(x.IsDeleted || !x.FirstName)))
            // }

            // // const contacts = [...Contacts, ...action.payload?.filter((x: Contact) => !Contacts?.map(y => y.ID)?.includes(x.ID))]?.filter((x: Contact) => !(x.IsDeleted || !x.FirstName))
            // state.Conversations = [...Conversations];
            // // state.Contacts = sortContacts([...contacts,]);
            // state.Groups = updateContactsInGroup(state.Groups, state.Contacts);
            // state.Settings = {
            //     ...state.Settings,
            //     BlockedContacts: Contacts?.filter((x: Contact) => x.IsBlocked),
            //     UnSubscribedContacts: Contacts?.filter((x: Contact) => x.IsUnsubscribed)
            // }

            let { Conversations, Contacts } = state;
            action.payload?.forEach((contact: Contact) => {
                let _conversation = Conversations?.find(x => x.PhoneNumber === contact.PhoneNumber || x.Contact?.ID === contact.ID);
                let _contact = Contacts?.find(x => x.ID === contact.ID);
                if (_conversation) {
                    _conversation.IsUnsubscribed = contact.IsUnsubscribed;
                    _conversation.UnReadCount = contact.UnReadCount;
                    _conversation.IsBlocked = contact.IsBlocked;
                    _conversation.Contact = contact
                }

                if (_conversation?.Contact?.ID === contact.ID && (contact.IsDeleted || contact.MaskedPhoneNumber !== _conversation?.Contact?.MaskedPhoneNumber)) {
                    _conversation.Contact = undefined;
                }

                if (_contact) {
                    if (_contact.IsBlocked !== contact.IsBlocked) {
                        _contact.IsBlocked = contact.IsBlocked;
                        _contact.CreatedDateTime = contact.CreatedDateTime;
                    }
                    _contact.UnReadCount = contact.UnReadCount;
                    _contact.IsDeleted = contact.IsDeleted;
                }
            });
            const filteredPayload = action.payload.map(y => y.ID);
            if (Contacts?.some((x) => filteredPayload.includes(x.ID))) {
                state.Contacts = sortContacts([
                    ...state.Contacts?.filter((x) => !filteredPayload?.includes(x.ID)),
                    ...action.payload,
                ]?.filter((x: Contact) => !(x.IsDeleted || !x.FirstName)));
                // state.AllContacts = [
                //     ...state.AllContacts?.filter((x) => !filteredPayload?.includes(x.ID)),
                //     ...action.payload,
                // ]
            }
            else {
                state.Contacts = sortContacts([...Contacts, ...action.payload?.filter((x: Contact) => !Contacts?.map(y => y.ID)?.includes(x.ID))]?.filter((x: Contact) => !(x.IsDeleted || !x.FirstName)))
                // state.AllContacts = [
                //     ...state.AllContacts?.filter((x) => !filteredPayload?.includes(x.ID)),
                //     ...action.payload,
                // ]
            }

            // const contacts = [...Contacts, ...action.payload?.filter((x: Contact) => !Contacts?.map(y => y.ID)?.includes(x.ID))]?.filter((x: Contact) => !(x.IsDeleted || !x.FirstName))
            state.Conversations = [...Conversations];
            // state.Contacts = sortContacts([...contacts,]);
            state.Groups = updateContactsInGroup(state.Groups, state.Contacts);

            const contactsSet = new Set(Contacts);
            const blockedList = []
            const unsubscribeList = []
            contactsSet?.forEach((c) => {
                if (c.IsBlocked) {
                    blockedList.push(c)
                }
                if (c.IsUnsubscribed) {
                    unsubscribeList.push(c)
                }
            })

            // state.Settings = {
            //     ...state.Settings,
            //     BlockedContacts: blockedList,
            //     UnSubscribedContacts: unsubscribeList
            // }

            state.GetUnSubscribedContacts = state.GetUnSubscribedContacts.filter(x => !action.payload.some(y => y.ID === x.ID)).concat(action.payload?.filter(x => x.IsUnsubscribed))
            state.GetBlockedContacts = state.GetBlockedContacts.filter(x => !action.payload.some(y => y.ID === x.ID)).concat(action.payload?.filter(x => x.IsBlocked))
        },

        pushGroupChat: (state: AppState, action: PayloadAction<GroupChatModel[]>) => {
            let { Conversations } = state;
            action.payload?.forEach((x: GroupChatModel) => {
                let con = Conversations?.find(y => y.GroupChat?.ID === x.ID)
                if (con) {
                    con.GroupChat.UnReadCount = x.UnReadCount;
                    con.GroupChat.Name = !!x?.Name ? x.Name : con.GroupChat.Name;
                }
            });
            state.Conversations = [...Conversations];
        },

        updateGroups: (state: AppState, action: PayloadAction<Group[]>) => {
            state.Groups = updateContactsInGroup(action.payload, state.Contacts);
            state.Contacts = updateGroupsInContacts(state.Contacts, action.payload);
            state.LoadingGroups = false;
            state.LoadedGroups = true;
            state.IsMassGroupsDeletionInProgress = false;
        },

        updateLoadingGroups: (state: AppState, action: PayloadAction<boolean>) => {
            state.LoadingGroups = action.payload
        },

        updateSecureDocs: (state: AppState, action: PayloadAction<SecureDocModel[]>) => {
            state.SecureDocs = action.payload;
            state.LoadingSecureDocs = false;
            state.LoadedSecureDocs = true;

        },

        updateLoadingSecureDocs: (state: AppState, action: PayloadAction<boolean>) => {
            state.LoadingSecureDocs = action.payload
        },

        pushGroups: (state: AppState, action: PayloadAction<Group[]>) => {
            let groups = [...state.Groups?.filter((x) => !action.payload?.map((y: Group) => y.ID)?.includes(x.ID)), ...action.payload]?.filter((x: Group) => !x.IsDeleted)
            state.Groups = updateContactsInGroup(groups, state.Contacts);
            state.Contacts = updateGroupsInContacts(state.Contacts, action.payload)
        },

        updateUnsubscribedContacts: (state: AppState, action: PayloadAction<Contact[]>) => {
            state.GetUnSubscribedContacts = action.payload;
        },

        updateBlockedContacts: (state: AppState, action: PayloadAction<Contact[]>) => {
            state.GetBlockedContacts = action.payload;
        },

        // updateProfile: (state: AppState, action: PayloadAction<ProfileModel>) => {
        //     state.Profile = { ...action.payload, OrganizationName: state.Profile?.OrganizationName };
        //     state.LoadingProfile = false;
        //     state.LoadedProfile = true;
        // },

        updatePlanMetrics: (state: AppState, action: PayloadAction<any>) => {
            const data = mapToPlanMetrics(action.payload, state.Settings.Settings)
            state.PlanMetrics = data as PlanMetrics;
        },

        // updateLoadingProfile: (state: AppState, action: PayloadAction<boolean>) => {
        //     state.LoadingProfile = action.payload;
        // },

        updateOrgName: (state: AppState, action: PayloadAction<ProfileModel>) => {
            // state.Profile = { ...state.Profile, OrganizationName: action.payload as any} as ProfileModel;
            state.OrganizationName = action.payload as any;
        },

        updateContact: (state: AppState, action: PayloadAction<Contact>) => {
            // state.Contacts = sortContacts([
            //     ...state.Contacts?.filter((x) => x.ID !== action.payload.ID),
            //     action.payload,
            // ]);
            state.Groups = updateContactsInGroup(state.Groups, [
                ...state.Contacts?.filter((x) => x.ID !== action.payload.ID),
                action.payload,
            ]);
            state.Conversations = sortConversations(
                [
                    ...state.Conversations?.filter(
                        (x) => x.PhoneNumber !== action.payload?.PhoneNumber
                    ),
                    {
                        ...state.Conversations?.find(
                            (x) => x.PhoneNumber === action.payload?.PhoneNumber
                        ),
                        Contact: { ...action.payload },
                    } as ConversationModel,
                ]?.filter((x) => x.PhoneNumber)
            )
        },

        updateConversation: (state: AppState, action: PayloadAction<ConversationModel>) => {
            state.Conversations = sortConversations([
                ...state.Conversations?.filter(
                    (x) => x.Campaign?.ID ? x.Campaign.ID !== action.payload?.PhoneNumber : x.GroupChat?.ID ? x.GroupChat.ID !== action.payload?.PhoneNumber : x.PhoneNumber !== action.payload?.PhoneNumber
                ),
                action.payload
            ])
        },

        updateSettings: (state: AppState, action) => {
            state.Settings = {
                ...state.Settings,
                Settings: action.payload,
                AllowedWrongPasswordAttempts: parseInt(
                    action.payload?.find(
                        (x: Setting) =>
                            x.Code === SettingsService.Wrong_Password_Attmps_Code
                    )?.Value || state.Settings?.AllowedWrongPasswordAttempts
                ),
                NotificationsEnabled: action.payload?.find(
                    (x: Setting) => x.Code === SettingsService.Notification_Code
                )
                    ? action.payload
                        ?.find(
                            (x: Setting) => x.Code === SettingsService.Notification_Code
                        )
                        .Value?.toLowerCase() === 'true'
                    : state.Settings.NotificationsEnabled,
                TwoFactAuthEnabled: action.payload?.find(
                    (x: Setting) => x.Code === SettingsService.Two_FactAuth_Code
                )
                    ? action.payload
                        ?.find(
                            (x: Setting) => x.Code === SettingsService.Two_FactAuth_Code
                        )
                        ?.Value.toLowerCase() === 'true'
                    : state.Settings.TwoFactAuthEnabled,
                UnSubscribeStopEnabled: action.payload?.find(
                    (x: Setting) => x.Code === SettingsService.Unsubscribe_Organization_All_PhoneNumbers
                )
                    ? action.payload
                        ?.find(
                            (x: Setting) => x.Code === SettingsService.Unsubscribe_Organization_All_PhoneNumbers
                        )
                        ?.Value.toLowerCase() === 'true'
                    : state.Settings.UnSubscribeStopEnabled,
                BlockIncomingMMSEnabled: action.payload?.find(
                    (x: Setting) => x.Code === SettingsService.Block_MMS_Code
                )
                    ? action.payload
                        ?.find(
                            (x: Setting) => x.Code === SettingsService.Block_MMS_Code
                        )
                        ?.Value.toLowerCase() === 'true'
                    : state.Settings.BlockIncomingMMSEnabled,
                BlockIncomingMessageLinksEnabled: action.payload?.find(
                    (x: Setting) => x.Code === SettingsService.Block_Links_Code
                )
                    ? action.payload
                        ?.find(
                            (x: Setting) => x.Code === SettingsService.Block_Links_Code
                        )
                        ?.Value.toLowerCase() === 'true'
                    : state.Settings.BlockIncomingMessageLinksEnabled,
                VoiceVerificationEnabled: action.payload?.find(
                    (x: Setting) => x.Code === SettingsService.Voice_Otp_Veification
                )
                    ? action.payload
                        ?.find(
                            (x: Setting) =>
                                x.Code === SettingsService.Voice_Otp_Veification
                        )
                        .Value?.toLowerCase() === 'true'
                    : state.Settings?.VoiceVerificationEnabled,
                AiFeaturesEnabled: action.payload?.find(
                    (x: Setting) => x.Code === "AI"
                )
                    ? action.payload
                        ?.find(
                            (x: Setting) =>
                                x.Code === "AI"
                        )
                        .Value?.toLowerCase() === 'true'
                    : state.Settings?.AiFeaturesEnabled,
            }
            if (!LocalStorage.get("current-user")?.isNewSubFlow) {
                const data = mapToPlanMetrics(LocalStorage.get("current-user")?.planTracker, action.payload)
                state.PlanMetrics = data as PlanMetrics;
            } else {
                const localStorage = LocalStorage.get("current-user")
                state.UserPlanTracker = mapToUserPlanTracker(localStorage.userPlanTracker, action.payload)
                state.OrgPlanTracker = mapToOrgPlanTracker(localStorage.orgPlanTracker)
            }

            let setTimeZone = action.payload?.find(x => x.Code === SettingsService.Auto_Time_Zone_Code)?.Value?.toString()?.toLowerCase() === "false" ? action.payload?.find(x => x.Code === SettingsService.Time_Zone_Code)?.Value?.toString() : UtilityService.getDefaultTimeZone()?.Name
            localStorage.setItem("Timezone", setTimeZone)
        },

        updateMenuFlag: (state: AppState, action: PayloadAction<boolean>) => {
            state.ShowSideMenu = action.payload
        },

        updateMobileDevice: (state: AppState) => {
            let width = window.outerWidth < 320 ? window.innerWidth : window.outerWidth
            state.IsMobileDevice = width <= 835 && width !== 0;
            state.DeviceWidth =
                width < 320
                    ? state.DeviceWidth < 320
                        ? 320
                        : state.DeviceWidth
                    : width;
            state.DeviceHeight = window.innerHeight
        },

        updateFeatures: (state: AppState, action: PayloadAction<FeaturesModel[]>) => {
            state.Features = action.payload;
            state.LoadingFeatures = false;
            state.LoadedFeatures = true;
        },

        updateLoadingFeatures: (state: AppState, action: PayloadAction<boolean>) => {
            state.LoadingFeatures = action.payload
        },

        updateOrganizations: (state: AppState, action: PayloadAction<OrganizationModel[]>) => {
            state.Organizations = action.payload;
            state.LoadingOrganizations = false;
            state.LoadedOrganizations = true;
        },

        updateLoadingOrganizations: (state: AppState, action: PayloadAction<boolean>) => {
            state.LoadingOrganizations = action.payload
        },

        updatePhoneNumber: (state: AppState, action: PayloadAction<PhoneNumberModel>) => {
            state.PhoneNumbers = [
                action.payload,
                ...state.PhoneNumbers?.filter((x) => x.ID !== action.payload.ID),
            ];
            state.LoadingPhoneNumbers = false;
            state.LoadedPhoneNumbers = true;
        },

        updatePhoneNumbers: (state: AppState, action: PayloadAction<PhoneNumberModel[]>) => {
            state.PhoneNumbers = action.payload;
            state.LoadingPhoneNumbers = false;
            state.LoadedPhoneNumbers = true;
        },

        updatePhoneNumbersGridData: (state: AppState, action: PayloadAction<PhoneNumberModel[]>) => {
            state.PhoneNumbers = action.payload;
            state.LoadingPhoneNumbers = false;
            state.LoadedPhoneNumbers = true;
        },


        updateLoadingPhoneNumbers: (state: AppState, action: PayloadAction<boolean>) => {
            state.LoadingPhoneNumbers = action.payload;
        },

        updateRoles: (state: AppState, action: PayloadAction<RoleModel[]>) => {
            state.Roles = action.payload;
        },

        updatePlans: (state: AppState, action: PayloadAction<PlanModel[]>) => {
            state.Plans = action.payload;
            state.LoadingPlans = false;
            state.LoadedPlans = true;
        },

        updateLoadingPlanes: (state: AppState, action: PayloadAction<boolean>) => {
            state.LoadingPlans = action.payload
        },

        updateProviders: (state: AppState, action: PayloadAction<ProviderModel[]>) => {
            state.Providers = action.payload;
            state.LoadingProviders = false;
            state.LoadedProviders = true;
        },
        updateLoadingProviders: (state: AppState, action: PayloadAction<boolean>) => {
            state.LoadingProviders = action.payload
        },

        headerTitle: (state: AppState, action) => {
            state.HeaderTitle = action.payload;
            state.HeaderCount = undefined;
            state.RefreshHandler = undefined;
            state.BackHandler = undefined
        },

        headerCount: (state: AppState, action: PayloadAction<number>) => {
            state.HeaderCount = action.payload
        },

        refreshHandler: (state: AppState, action) => {
            state.RefreshHandler = action.payload
        },

        backHandler: (state: AppState, action) => {
            state.BackHandler = action.payload;
        },

        headerSelectedCount: (state: AppState, action) => {
            state.HeaderSelectedCount = action.payload;
        },

        updateAlertNotification: (state: AppState, action: PayloadAction<AlertNotificationModel[]>) => {
            state.AlertNotifications = action.payload;
            state.LoadingAlertsNotications = false;
        },

        updateLoadingAlertNotification: (state: AppState, action: PayloadAction<boolean>) => {
            state.LoadingAlertsNotications = action.payload
        },

        updateUserAlertNotifaction: (state: AppState, action: PayloadAction<AlertNotificationModel[]>) => {
            state.UserAlertNotifications = action.payload
        },

        setShowingAlert: (state: AppState, action: PayloadAction<number>) => {
            state.ShowingAlertsCount = action.payload
        },

        updateLoadingKeywords: (state: AppState, action: PayloadAction<boolean>) => {
            state.LoadingAutoAlertKeywords = action.payload;
        },

        updateKeywords: (state: AppState, action: PayloadAction<AutoAlertKeywordModel[]>) => {
            state.RulesKeywords = action.payload?.filter(
                (x: AutoAlertKeywordModel) => !x.IsDeleted
            );
            state.AllKeywords = action.payload;
            state.LoadingAutoAlertKeywords = false;
        },

        updateTemplates: (state: AppState, action: PayloadAction<{ data: any, refresh: boolean }>) => {
            let { Templates } = state;
            let templatesData = action.payload.refresh ? action.payload.data : [...Templates?.filter(x => x.ID !== action.payload.data?.ID), action.payload.data]
            state.Templates = templatesData.sort((x: TemplateModel, y: TemplateModel) => new Date(x.UpdatedDateTime) <= new Date(y.UpdatedDateTime) ? 1 : -1);
            state.LoadingTemplates = false;
        },

        updateLoadingTemplates: (state: AppState, action: PayloadAction<boolean>) => {
            state.LoadingTemplates = action.payload;
        },

        updateUsers: (state: AppState, action: PayloadAction<UserModel[]>) => {
            let regex =
                /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            state.Users = action.payload?.filter((x: UserModel) =>
                regex.test(String(x.Email).toLowerCase())
            );
            state.AllUsers = action.payload;
            state.LoadingUsers = false;
            state.LoadedUsers = true;
        },

        updateLoadingUsers: (state: AppState, action: PayloadAction<boolean>) => {
            state.LoadingUsers = false
        },

        updateLoadingPrivateAutomationRules: (state: AppState, action: PayloadAction<boolean>) => {
            state.LoadingAutomationPrivateRules = action.payload
        },

        updateLoadingGlobalAutomationRules: (state: AppState, action: PayloadAction<boolean>) => {
            state.LoadingAutomationGlobalRules = action.payload;
        },

        updatePrivateAutomationRules: (state: AppState, action: PayloadAction<RuleModel[]>) => {
            const data = action.payload?.filter((x: RuleModel) => !x.IsDeleted)
            const sortedData = data.sort((a, b) => {
                if (a.Status !== b.Status) {
                    return a.Status - b.Status;
                }
                return new Date(b.CreatedDateTime).getTime() - new Date(a.CreatedDateTime).getTime();
            });
            state.PrivateRules = sortedData;
            state.AllPrivateRules = action.payload;
            state.LoadingAutomationPrivateRules = false;
        },

        updateGlobalAutomationRules: (state: AppState, action: PayloadAction<RuleModel[]>) => {
            state.GlobalRules = action.payload?.filter((x: RuleModel) => !x.IsDeleted);
            state.AllGlobalRules = action.payload;
            state.LoadingAutomationGlobalRules = false;
        },

        updateReports: (state: AppState, action: PayloadAction<ReportsModel[]>) => {
            state.Reports = action.payload;
            state.LoadingReports = false;
        },

        loadingReports: (state: AppState, action: PayloadAction<boolean>) => {
            state.LoadingReports = action.payload
        },

        updatePlanHistory: (state: AppState, action: PayloadAction<PlanHistoryModel[]>) => {
            state.PlanHistory = action.payload
        },

        setKafka: (state: AppState, action: PayloadAction<KafkaModel[]>) => {
            state.Kafka = action.payload?.sort((a: KafkaModel, b: KafkaModel) => a.GroupId > b.GroupId ? 1 : -1)
        },

        updateKafka: (state: AppState, action: PayloadAction<KafkaModel>) => {
            state.Kafka = [...state.Kafka?.filter(x => x.GroupId !== action.payload?.GroupId), action.payload]
                .sort((a: KafkaModel, b: KafkaModel) => a.GroupId > b.GroupId ? 1 : -1)
        },

        loadingKafkaOrg: (state: AppState, action: PayloadAction<boolean>) => {
            state.LoadingKafka = action.payload
        },

        groupContactsDeletion: (state: AppState, action: PayloadAction<DeletedGroupContactData[]>) => {
            state.GroupContactsDeletionData = action.payload
        },

        updateTypingData: (state: AppState, action: PayloadAction<TypingDataModel>) => {
            const data = action.payload as TypingDataModel;
            state.TypingData = data.Action === TypeAction.START_TYPING ? [...state.TypingData.filter(x => x.UserID !== data.UserID), data] : state.TypingData.filter(x => x.ReceiverPhoneNumber !== data.ReceiverPhoneNumber || x.UserID !== data.UserID)
        },

        updateMassContactsDeletionInProgess: (state, action) => {
            state.IsMassContactsDeletionInProgress = action.payload
        },

        handleMassContactsActionEvent: (state, action) => {
            state.HandleMassContactsActionEvent = action.payload
        },

        updateMassGroupsDeletionInProgess: (state, action) => {
            state.IsMassGroupsDeletionInProgress = action.payload
        },

        handleMassGroupsActionEvent: (state, action) => {
            state.HandleMassGroupsActionEvent = action.payload
        },

        loadingSecureFiles: (state, action) => {
            state.LoadingSecureFiles = action.payload
        },

        loadingFaceAuthenticationFiles: (state, action) => {
            state.LoadingFaceAuthenticationFiles = action.payload
        },

        updateFaceAuthenticationFiles: (state, action) => {
            state.LoadingFaceAuthenticationFiles = false;
            let FaceAuthenticationFiles = state.FaceAuthenticationFiles.filter(x => !action.payload?.some(y => y.Id === x.Id)).concat(action.payload)
            state.FaceAuthenticationFiles = FaceAuthenticationFiles?.filter(x => x.Status !== FaceRegistrationInviteLinkStatus.Delete)
        },

        updateSecureFiles: (state, action) => {
            state.SecureFiles = action.payload;
            state.LoadingSecureFiles = false;
        },

        loadingSecureFilesLinks: (state, action) => {
            state.LoadingSecureFileLinks = action.payload
        },

        updateSecureFilesLinks: (state, action) => {
            state.SecureFileLinks = action.payload;
            state.LoadingSecureFileLinks = false;
        },
        updateActivityFeedNotificationStatus: (state, action) => {
            state.ActivityFeedNotificationStatus = action.payload
        },
        handlePushCampaignChatName: (state, action) => {
            let { Conversations } = state;
            let con = Conversations.find(y => y.Campaign?.ID === action.payload.id)
            if (con) {
                con.Campaign.Name = action.payload?.name;
            }
            state.Conversations = Conversations
        },
        loadingscheduleMessages: (state: AppState, action: PayloadAction<boolean>) => {
            state.LoadingscheduleMessages = action.payload
        },

        updateScheduleMessages: (state, action) => {
            let scheduleMsg = action.payload.map(x => mapToScheduledMessage(x, state.Contacts, state.Groups))
            let fillterscheduleMsg = state.ScheduleMessages.filter(x => !scheduleMsg.some(y => y.ID === x.ID)).concat(scheduleMsg)
            state.ScheduleMessages = fillterscheduleMsg.filter(x => (!(x.IsDeleted || x.Status === ScheduleMessageStatus.SENT)))
            state.LoadingscheduleMessages = false
        },

        updatePhonenumberSelectedOrgIDs: (state: AppState, action) => {
            state.PhonenumberSelectedOrgIDs = action.payload
        },
        updateWebexNotificationStatus: (state, action) => {
            state.WebexNotificationStatus = action.payload
        },
        updateNotificationStatus: (state, action) => {
            state.NotificationStatus = action.payload
        },
        updateOrganizationsphoneNumbers: (state, action) => {
            state.OrganizationsphoneNumbers = action.payload
        },
        updateUsersSelectedOrgIDs: (state: AppState, action) => {
            state.UsersSelectedOrgIDs = action.payload
        },

        updateOrganizationsUsers: (state, action) => {
            state.OrganizationsUsers = action.payload
        },



        //toaster functions
        setToasts: (state, action) => {
            state.Toasts = [
                {
                    ID: action.payload.id,
                    Text: action.payload.text,
                    Timeout: action.payload.timeout,
                    Type: action.payload.type,
                    Hide: true,
                },
                ...state.Toasts
            ]
        },
        showToast: (state, action) => {
            let x = state.Toasts.find((x) => x.ID === action.payload.id)
            if (x) {
                x.Hide = false
            }

        },
        hideToast: (state, action) => {
            let x = state.Toasts.find((x) => x.ID === action.payload.id)
            if (x) {
                x.Hide = true
            }
        },
        clearToast: (state, action) => {
            state.Toasts = state.Toasts.filter((x) => x.ID !== action.payload.id)
        },
        LoadMsgLogsExports: (state, action) => {
            state.ExportMessageLogs = action.payload
        },
        LoadSecureChatMsgLogsExports: (state, action) => {
            state.SecureChatExportMessageLogs = action.payload
        },
        pushMessageLogsDownloadReport: (state, action) => {
            state.IsMessageLogDownloadReportMsg = action.payload
        },
        HandleWebNotication: (state, action) => {
            state.WebNotificationText = action.payload
        },

        updateUnsubscribedContactsFromSignalR: (state: AppState, action: PayloadAction<Contact>) => {
            let filterUnSubContact = state.GetUnSubscribedContacts?.find(x => action.payload?.ID === x.ID)
            if (!!filterUnSubContact) {
                if (action.payload?.IsUnsubscribed === false) {
                    state.GetUnSubscribedContacts = state.GetUnSubscribedContacts?.filter(x => x?.ID !== filterUnSubContact?.ID)
                }
            }
            else {
                if (!!action.payload && action.payload?.IsUnsubscribed === true) {
                    state.GetUnSubscribedContacts = state.GetUnSubscribedContacts.concat(action.payload)
                }
            }
        },

        getBusinessHoursStatus: (state, action) => {
            state.BusinessHoursStatus = action.payload
        },

        updateAssignToUser: (state: AppState, action: PayloadAction<any>) => {
            state.AssignToUser = action.payload
        },

        ClearContactStatusHirtory: (state: AppState) => {
            state.ContactStatusHistory = {}
        },

        updateLowCreditSettings: (state: AppState, action: PayloadAction<LowCreditSettingsModel>) => {
            state.LowCreditSettings = action.payload
        },

        updateIntegationsyncing: (state: AppState) => {
            state.IscontactsSyncing = true;
            state.BulkImportSyncDetails = null;
        },

        updateGroupIntegationsyncing: (state: AppState) => {
            state.IsGroupContactsSyncing = true;
        },

        updateIntegrationDetails: (state: AppState, action: PayloadAction<BulkImportReportModel>) => {
            state.IscontactsSyncing = false;
            state.IsGroupContactsSyncing = false;
            state.BulkImportSyncDetails = mapToIntegrationReport(action.payload);
        },

        updateSignalRConnectionCOnnectionState: (state: AppState, action: PayloadAction<boolean>) => {
            state.isSignalRConnected = action.payload;
        },
        updateTheme: (state: AppState, action: PayloadAction<boolean>) => {
            state.isDarkMode = action.payload;
        },

        updateNewLoadingPlanes: (state: AppState, action: PayloadAction<boolean>) => {
            state.LoadingNewPlans = action.payload
        },

        updateNewPlans: (state: AppState, action: PayloadAction<Plan[]>) => {
            state.newPlans = action.payload;
            state.LoadingNewPlans = false;
            state.LoadedNewPlans = true;
        },
        updateLoadingNewOrganizations: (state: AppState, action: PayloadAction<boolean>) => {
            state.LoadingNewOrganizations = action.payload
        },

        updateSettingsFromSignalR: (state: AppState, action: PayloadAction<any>) => {
            const existingSettings = state.Settings.Settings;
            const existingMap = new Map(existingSettings.map(item => [item.Code, item]));
            action.payload.forEach(payloadItem => {
                const existingItem = existingMap.get(payloadItem.Code);
                if (existingItem) {
                    Object.assign(existingItem, payloadItem);
                } else {
                    existingSettings.push(payloadItem);
                }
            });
        },

        updatePhoneNumberGroups: (state: AppState, action: PayloadAction<PhoneNumbergroup[]>) => {
            state.PhoneNumberGroups = action.payload;
        },

        updatePhoneGroupsSelectedOrgIDs: (state: AppState, action) => {
            state.PhoneGroupsSelectedOrgIDs = action.payload
        },

        updateOrganizationPhoneNumberGroups: (state: AppState, action: PayloadAction<PhoneNumbergroup[]>) => {
            state.OrganzationPhoneNumberGroups = action.payload;
        },

        updateTrainChatHistoryStatus: (state: AppState, action: PayloadAction<string>) => {
            state.TrainChatHistoryStatus = action.payload;
        },

        updateAiTrainedFileStatus: (state: AppState, action: PayloadAction<any>) => {
            state.AiTrainedFiles = [...state.AiTrainedFiles.filter(x => x.file_name !== action.payload.file_name), action.payload]
        },

        updateAiTrainedUrlStatus: (state: AppState, action: PayloadAction<any>) => {
            action.payload.forEach(newItem => {
                state.AiTrainedUrls = [
                    ...state.AiTrainedUrls.filter(x => x.url !== newItem.url),
                    newItem
                ];
            });
        },

        updateAiTrainedFileList: (state: AppState, action: PayloadAction<[]>) => {
            state.AiTrainedFiles = action.payload;
        },

        updateAiTrainedUrlList: (state: AppState, action: PayloadAction<[]>) => {
            state.AiTrainedUrls = action.payload;
        },

        updateNewOrganizations: (state: AppState, action: PayloadAction<NewOrganization[]>) => {
            state.NewOrganizations = action.payload;
            state.LoadingNewOrganizations = false;
        },
        setIsNewOrgFlow: (state: AppState, action: PayloadAction<boolean>) => {
            state.IsNewOrgFlow = action.payload
        },
        updateOrgPlanMetrics: (state: AppState, action: PayloadAction<OrgPlanTracker>) => {
            state.OrgPlanTracker = { ...action.payload } as OrgPlanTracker;
        },

        updateRoutes: (state: AppState, action: PayloadAction<RouteModel[]>) => {
            if (state.IsTeamsApp && state.IsMobileDevice) {
                state.Routes = TeamsMobileRoutes();
            } else if (state.IsTeamsApp) {
                state.Routes = TeamRoutes();
            } else if (state.IsMobileDevice) {
                state.Routes = MobileRoutes();
            } else {
                state.Routes = Routes();
            }
        },
        updateUserPlanMetrics: (state: AppState, action: PayloadAction<UserPlanTracker>) => {
            const data = mapToUserPlanTracker(action.payload, state.Settings.Settings)
            state.UserPlanTracker = data as UserPlanTracker;
        },

        updateOptInCollectedMessage: (state: AppState, action: PayloadAction<any>) => {
            state.OptInCollectedMessage = action.payload
        },

        updateBulkImportFilesDetails: (state: AppState, action: PayloadAction<BulkImportReportModel>) => {
            state.BulkImportSyncDetails = mapToBulkImportReport(action.payload);
        },

        updateIsScheduleMessageImporting: (state: AppState, action: PayloadAction<boolean>) => {
            state.IsScheduleMessageImporting = action.payload;
        },

        loadSecureChatScheduleMessages: (state: AppState, action: PayloadAction<[]>) => {
            state.SecureChatScheduleMessages = action.payload;
        },

        updateSecureChatScheduleMessages: (state: AppState, action: PayloadAction<any>) => {
            state.SecureChatScheduleMessages = [...state.SecureChatScheduleMessages.filter(x => x.ID != action.payload.ID), action.payload]
        },

        updateBlockedContactsFormSignalR: (state: AppState, action: PayloadAction<Contact[]>) => {
            state.IsMassContactsDeletionInProgress = false;
            state.Conversations = updateContactsInConversations(
                state.Conversations,
                action.payload
            );
            state.Groups = updateContactsInGroup(state.Groups, action.payload);

            const payloadContacts = new Map(action.payload.map(contact => [contact.ID, contact]));

            const payloadIds = new Set(action.payload.map(c => c.ID));

            state.GetBlockedContacts = [...action.payload, ...state.GetBlockedContacts.filter(x => !payloadIds.has(x.ID))].filter(x => x.IsBlocked);

            state.Contacts = state.Contacts.map(x => {
                let matchingContact = payloadContacts.get(x.ID);
                if (!!matchingContact) {
                    return { ...x, IsBlocked: matchingContact.IsBlocked }
                }
                else { return x }
            })
        },

        updateConversationsAssignStatus: (state: AppState, action: PayloadAction<any>) => {
            state.ConversationsAssignStatus = {
                ...state.ConversationsAssignStatus,
                [action.payload.ContactNumber]: action.payload
            }
        },

        updateNewSubscriptionOrganizationsphoneNumbers: (state, action) => {
            state.NewSubscriptionOrganizationsphoneNumbers = action.payload
        },

        updateNewSubscriptionPhonenumberSelectedOrgIDs: (state: AppState, action) => {
            state.NewSubscriptionPhonenumberSelectedOrgIDs = action.payload
        },

        updateNewSubscriptionSelectedUsersOrgIds: (state: AppState, action) => {
            state.NewSubscriptionSelectedUsersOrgIds = action.payload
        },

        updateNewSubscriptionOrganizationsUsers: (state, action) => {
            state.NewSubscriptionOrganizationsUsers = action.payload
        },

        updateFalkonSignNotificationStatus: (state, action) => {
            state.IsFalkonSignNotification = action.payload
        },

        updateFalkonEsignTemplates: (state, action) => {
            state.FalkonEsignTemplates = action.payload
        },

        updateWebForms: (state: AppState, action: PayloadAction<WebFormModel[]>) => {
            let { WebForms } = state;
            let data = [...WebForms.filter(x => !action.payload.some(y => y.Id === x.Id)), ...action.payload].filter(x => !x.IsDeleted);
            state.WebForms = data.sort((x: WebFormModel, y: WebFormModel) => new Date(x.UpdatedDateTime) <= new Date(y.UpdatedDateTime) ? 1 : -1);

        },

        updateOrganizationLoginType: (state: AppState, action: PayloadAction<LoginTypeModel>) => {
            state.OrganizationLoginType = action.payload
        },

        updateArchiveConfigurableDaysSetting: (state: AppState, action: PayloadAction<ArchiveConfigurableDaysSetting>) => {
            state.ArchiveConfigurableDaysSetting = action.payload
        },
    }

})

export const {
    updateUserRoleId,
    updateConversations,
    updateSecureChatConversations,
    UpdateWebSocket,
    UpdateWebSocketConnection,
    updateMuteConversations,
    updateArchiveConversations,
    updateContactTypes,
    updateContactTypesInConversation,
    updateContacts,
    updateContactsInConversation,
    updateCustomAttributeDefinitions,
    updateConversation,
    updateFeatures,
    updateGlobalAutomationRules,
    updateLoadingConversations,
    updateLoadingSecureChatConversations,
    updateGroups,
    updatePhoneNumber,
    updatePlanHistory,
    updatePlanMetrics,
    updatePlans,
    updatePrivateAutomationRules,
    // updateProfile,
    updateProviders,
    updateReports,
    updateRoles,
    updateSettings,
    updateUnsubscribedContacts,
    updateUserAlertNotifaction,
    updateHistory,
    updateChatSummaryHistory,
    updateKeywords,
    updateLoadingAlertNotification,
    updateLoadingContacts,
    updateLoadingFeatures,
    updateLoadingGlobalAutomationRules,
    updateLoadingGroups,
    updateLoadingKeywords,
    updateLoadingOrganizations,
    updateLoadingPhoneNumbers,
    updateLoadingPlanes,
    updateLoadingPrivateAutomationRules,
    // updateLoadingProfile,
    updateLoadingProviders,
    updateMobileDevice,
    updateOrgName,
    updateOrganizations,
    updatePhoneNumbers,
    updateTemplates,
    updateLoadingTemplates,
    updateMenuFlag,
    updateUsers,
    updateLoadingUsers,
    clearHistory,
    loadingReports,
    pushContacts,
    pushConversations,
    backHandler,
    headerCount,
    headerTitle,
    refreshHandler,
    setShowingAlert,
    updateAlertNotification,
    updateBlockedContacts,
    updateContact,
    loadingKafkaOrg,
    setKafka,
    updateKafka,
    cleanHistory,
    pushGroups,
    pushGroupChat,
    groupContactsDeletion,
    updateTypingData,
    updateMassContactsDeletionInProgess,
    updateMassGroupsDeletionInProgess,
    loadingSecureFiles,
    loadingFaceAuthenticationFiles,
    updateSecureFiles,
    loadingSecureFilesLinks,
    updateSecureFilesLinks,
    updateNewMessageRecived,
    pushMessageLogsDownloadReport,
    handlePushCampaignChatName,
    clearToast,
    hideToast,
    setToasts,
    showToast,
    LoadMsgLogsExports,
    LoadSecureChatMsgLogsExports,
    HandleWebNotication,
    updatePhoneNumbersGridData,
    updateScheduleMessages,
    loadingscheduleMessages,
    updateDataLoading,
    updatePhonenumberSelectedOrgIDs,
    updateActivityFeedNotificationStatus,
    updateNotificationStatus,
    updateWebexNotificationStatus,
    updateUsersSelectedOrgIDs,
    updateOrganizationsphoneNumbers,
    updateOrganizationsUsers,
    headerSelectedCount,
    updateUnsubscribedContactsFromSignalR,
    getBusinessHoursStatus,
    updatePinConversations,
    updateFaceAuthenticationFiles,
    updateAssignToUser,
    updateContactHistory,
    updateContactStatusHistory,
    ClearContactStatusHirtory,
    updateLowCreditSettings,
    updateIntegationsyncing,
    updateIntegrationDetails,
    updateSecureDocs,
    updateLoadingSecureDocs,
    updateSignalRConnectionCOnnectionState,
    updateTheme,
    updatePhoneNumberGroups,
    updateOrganizationPhoneNumberGroups,
    updatePhoneGroupsSelectedOrgIDs,
    updateSettingsFromSignalR,
    updateTrainChatHistoryStatus,
    updateAiTrainedFileList,
    updateAiTrainedUrlList,
    updateAiTrainedFileStatus,
    updateAiTrainedUrlStatus,
    updateNewLoadingPlanes,
    updateNewPlans,
    updateLoadingNewOrganizations,
    updateNewOrganizations,
    setIsNewOrgFlow,
    updateOrgPlanMetrics,
    updateRoutes,
    updateUserPlanMetrics,
    updateOptInCollectedMessage,
    updateIsScheduleMessageImporting,
    updateBulkImportFilesDetails,
    loadSecureChatScheduleMessages,
    updateSecureChatScheduleMessages,
    updateBlockedContactsFormSignalR,
    updateConversationsAssignStatus,
    updateNewSubscriptionOrganizationsphoneNumbers,
    updateNewSubscriptionPhonenumberSelectedOrgIDs,
    updateNewSubscriptionOrganizationsUsers,
    updateNewSubscriptionSelectedUsersOrgIds,
    updateFalkonSignNotificationStatus,
    updateGroupIntegationsyncing,
    updateFalkonEsignTemplates,
    updateWebForms,
    updateOrganizationLoginType,
    updateArchiveConfigurableDaysSetting,
} = counterSlice.actions

export default counterSlice.reducer
